import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import Popup from 'components/popup';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { useToast } from 'components/ui/use-toast';
import { adminContent } from 'services/api';
import type {
  AdminFlowCompanionSummary,
  ClearFlowCompanionResponse,
} from 'services/api/adminContentClient';
import type { AdminUserListItem } from 'pages/Admin/UserManagement/types';

type FlowCompanionToolsProps = {
  selectedUser: AdminUserListItem | null;
};

const formatTimestamp = (value: string | null) => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleString();
};

const getUserDisplayName = (user: AdminUserListItem) => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email;
};

const FlowCompanionTools: React.FC<FlowCompanionToolsProps> = ({ selectedUser }) => {
  const { addToast } = useToast();
  const [summaries, setSummaries] = useState<AdminFlowCompanionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clearingFlowId, setClearingFlowId] = useState<number | null>(null);
  const [pendingClearSummary, setPendingClearSummary] = useState<AdminFlowCompanionSummary | null>(null);

  const loadSummaries = useCallback(async (userId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminContent.userTools.listFlowCompanionSessions(String(userId));
      setSummaries(response.data);
    } catch (err: any) {
      setSummaries([]);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          'Unable to load Module Companion sessions.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedUser) {
      setSummaries([]);
      setError(null);
      setLoading(false);
      setPendingClearSummary(null);
      return;
    }

    void loadSummaries(selectedUser.id);
  }, [loadSummaries, selectedUser]);

  const handleClear = useCallback(
    async () => {
      if (!selectedUser || !pendingClearSummary) {
        return;
      }

      setClearingFlowId(pendingClearSummary.flow_id);
      try {
        const response = await adminContent.userTools.clearFlowCompanion({
          user_id: selectedUser.id,
          flow_id: pendingClearSummary.flow_id,
        });
        const result = response.data as ClearFlowCompanionResponse;
        addToast({
          title: 'Module Companion chat cleared',
          description: `${result.flow_name}: archived ${result.archived_session_count} session(s) and ${result.archived_message_count} message(s).`,
        });
        setPendingClearSummary(null);
        await loadSummaries(selectedUser.id);
      } catch (err: any) {
        addToast({
          title: 'Failed to clear Module Companion chat',
          description:
            err?.response?.data?.error ||
            err?.response?.data?.detail ||
            'Please try again later.',
          variant: 'destructive',
        });
      } finally {
        setClearingFlowId(null);
      }
    },
    [addToast, loadSummaries, pendingClearSummary, selectedUser],
  );

  if (!selectedUser) {
    return (
      <div className="flex min-h-[240px] items-center justify-center text-sm text-muted-foreground">
        Select a user above to manage their Module Companion chats.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <LoadingAnimation />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-foreground">
          Module Companion Chats for {getUserDisplayName(selectedUser)}
        </h2>
        <p className="text-sm text-muted-foreground">
          Archive active companion chats for a module when the user needs a fresh session, including
          crisis lock resets.
        </p>
      </div>

      {error ? (
        <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {summaries.length === 0 ? (
        <div className="rounded border border-border/40 bg-muted/10 px-4 py-8 text-center text-sm text-muted-foreground">
          No active Module Companion chats found for this user.
        </div>
      ) : (
        <div className="space-y-3">
          {summaries.map((summary) => (
            <div
              key={summary.flow_id}
              className="rounded-xl border border-border/40 bg-background px-4 py-4 shadow-sm"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground">{summary.flow_name}</h3>
                    {summary.has_crisis_lock ? (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Crisis locked
                      </Badge>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span>{summary.active_session_count} active session(s)</span>
                    <span>{summary.active_message_count} message(s)</span>
                    <span>Latest activity: {formatTimestamp(summary.latest_session_updated_at)}</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="secondary"
                  size="auto"
                  onClick={() => setPendingClearSummary(summary)}
                  disabled={clearingFlowId === summary.flow_id}
                >
                  {clearingFlowId === summary.flow_id ? 'Clearing...' : 'Clear Chat'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pendingClearSummary ? (
        <Popup
          onClose={() => setPendingClearSummary(null)}
          title="Clear Module Companion Chat?"
          description={`Archive active Module Companion chats for ${getUserDisplayName(selectedUser)} in "${pendingClearSummary.flow_name}"? The next companion session will start fresh.`}
          isSubmitting={clearingFlowId !== null}
          size="wide"
          contentLayout="form"
          actionsLayout="end"
          actions={
            <div className="flex flex-wrap justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                size="auto"
                onClick={() => setPendingClearSummary(null)}
                disabled={clearingFlowId !== null}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="auto"
                onClick={() => void handleClear()}
                disabled={clearingFlowId !== null}
              >
                {clearingFlowId !== null ? 'Clearing...' : 'Confirm Clear Chat'}
              </Button>
            </div>
          }
        >
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-foreground">
            This archives {pendingClearSummary.active_session_count} active session(s) and preserves
            existing message history for admin review.
          </div>
        </Popup>
      ) : null}
    </div>
  );
};

export default FlowCompanionTools;
